"use client";

import { useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, Pause, X } from "lucide-react";
import Link from "next/link";
import { Button } from "@/app/_module/components/ui/button";
import {
  managedMarketingApi,
  type Campaign,
  type Workspace,
} from "./managedMarketingApi";
import {
  activityText,
  dateLabel,
  dollarsToCents,
  errorMessage,
  money,
  safeWebUrl,
  statusLabels,
} from "./marketingHelpers";
import CampaignOperations from "./CampaignOperations";
import CampaignFollowup from "./CampaignFollowup";
import s from "./MarketingWorkspace.module.css";

export default function CampaignDetailPanel({
  id,
  ownerId,
  operator,
  workspace,
  mobile,
  onClose,
}: {
  id: string;
  ownerId: string;
  operator: boolean;
  workspace: Workspace;
  mobile: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: [
      "managed-marketing",
      ownerId,
      operator ? "operation" : "campaign",
      id,
    ],
    queryFn: () => managedMarketingApi.detail(id, operator),
    staleTime: 15_000,
    retry: 1,
    refetchInterval: (query) =>
      query.state.data?.funding.some(
        (payment) =>
          payment.status === "pending" &&
          Date.parse(payment.expiresAt) > Date.now(),
      )
        ? 10_000
        : false,
  });
  const [section, setSection] = useState<"activity" | "funds" | "leads">(
    "activity",
  );
  const [action, setAction] = useState<
    "fund" | "change" | "approve" | "pause" | null
  >(null);
  const [notice, setNotice] = useState("");
  const paymentRequest = useRef<{ amount: number; id: string } | null>(null);
  const mutation = useMutation({
    mutationFn: async ({
      kind,
      campaign,
      form,
    }: {
      kind: NonNullable<typeof action>;
      campaign: Campaign;
      form: FormData;
    }) => {
      if (kind === "approve")
        return managedMarketingApi.approve(id, campaign.creativeVersion);
      if (kind === "pause") return managedMarketingApi.pause(id);
      if (kind === "change")
        return managedMarketingApi.changeRequest(
          id,
          String(form.get("message")).trim(),
        );
      const amount = dollarsToCents(String(form.get("amount")));
      const remaining =
        campaign.requestedBudgetCents -
        campaign.fundedCents +
        campaign.refundedCents;
      if (
        amount < 50 ||
        (amount < workspace.settings.minimumBudgetCents &&
          amount !== remaining) ||
        amount > workspace.settings.maximumBudgetCents
      )
        throw new Error(
          `Add at least ${money(workspace.settings.minimumBudgetCents)}, or pay the exact remaining budget of ${money(remaining)} (minimum payment $0.50).`,
        );
      const existing = query.data?.funding.find(
        (payment) =>
          payment.amountCents === amount && payment.status === "pending",
      );
      const previous = query.data?.funding.find(
        (payment) => payment.id === paymentRequest.current?.id,
      );
      if (
        paymentRequest.current?.amount !== amount ||
        (previous && previous.status !== "pending")
      )
        paymentRequest.current = {
          amount,
          id: existing?.id || crypto.randomUUID(),
        };
      const checkout = await managedMarketingApi.checkout(
        id,
        amount,
        paymentRequest.current.id,
      );
      const url = safeWebUrl(checkout.checkoutUrl);
      if (!url || new URL(url).hostname !== "checkout.stripe.com")
        throw new Error(
          "The payment link could not be verified. Please try again.",
        );
      window.location.assign(url);
      return null;
    },
    onError: () =>
      client.invalidateQueries({ queryKey: ["managed-marketing", ownerId] }),
    onSuccess: async (_, variables) => {
      if (variables.kind === "fund") return;
      await client.invalidateQueries({
        queryKey: ["managed-marketing", ownerId],
      });
      setNotice(
        variables.kind === "approve"
          ? "Campaign approved. Our team will handle the launch once funding is confirmed."
          : variables.kind === "pause"
            ? "Pause requested. The campaign remains active until our team confirms it is paused."
            : "Your change request has been sent to our team.",
      );
      setAction(null);
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (query.data && action)
      mutation.mutate({
        kind: action,
        campaign: query.data.campaign,
        form: new FormData(event.currentTarget),
      });
  }
  function openAction(kind: NonNullable<typeof action>) {
    mutation.reset();
    setNotice("");
    setAction(kind);
  }
  if (query.isPending)
    return (
      <div className={s.detail} role="status">
        <p className="p-6">Loading campaign…</p>
      </div>
    );
  if (query.isError)
    return (
      <div className={s.notice} role="alert">
        {errorMessage(query.error)}
        <Button
          variant="outline"
          className="ml-3"
          onClick={() => query.refetch()}
        >
          Try again
        </Button>
      </div>
    );
  const { campaign, events, ledger, leads, funding } = query.data;
  const creative = campaign.creative;
  const closed =
    campaign.status === "completed" || campaign.status === "rejected";
  const landingUrl = safeWebUrl(creative?.landingUrl);
  return (
    <section className={s.detail} aria-labelledby={`campaign-title-${id}`}>
      <header className={s.detailHeader}>
        <div>
          <span className={s.status} data-status={campaign.status}>
            {statusLabels[campaign.status]}
          </span>
          <h2
            id={`campaign-title-${id}`}
            className="mt-2 text-xl font-semibold tracking-tight"
          >
            {campaign.name}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {campaign.profileSnapshot.name} · {campaign.states.join(", ")} ·
            Requested {dateLabel(campaign.createdAt)}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close campaign details"
        >
          <X />
        </Button>
      </header>
      <div className={s.detailGrid}>
        <div className={s.preview}>
          <div className="mb-5 flex items-center gap-2">
            <img
              alt=""
              src="/brands/licensedproducer/logo.svg"
              className="h-8 w-8"
            />
            <div>
              <p className="text-sm font-semibold">Licensedproducer</p>
              <p className="text-xs text-muted-foreground">
                {creative
                  ? `Creative preview · Version ${campaign.creativeVersion}`
                  : "Your campaign creative"}
              </p>
            </div>
          </div>
          {creative ? (
            <>
              <p className="mb-4 whitespace-pre-wrap text-sm leading-relaxed">
                {creative.body}
              </p>
              {safeWebUrl(creative.imageUrl) && (
                <img
                  src={creative.imageUrl}
                  className={s.previewImage}
                  alt={`Campaign creative for ${campaign.profileSnapshot.name}`}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              )}
              <h3 className="mt-4 text-lg font-semibold">
                {creative.headline}
              </h3>
              {landingUrl && (
                <a
                  href={landingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm underline underline-offset-4"
                >
                  Preview landing page
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                Placement may change the crop and layout. This is the copy and
                image you approve.
              </p>
            </>
          ) : (
            <div className="py-10">
              <h3 className="text-base font-medium">
                Your creative is being prepared.
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Our team will use your profile and campaign brief. You’ll review
                it here before it’s approved for launch.
              </p>
            </div>
          )}
          {campaign.approvedCreativeVersion === campaign.creativeVersion &&
            campaign.creativeVersion > 0 && (
              <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <Check className="h-4 w-4" />
                You approved version {campaign.approvedCreativeVersion}
              </p>
            )}
        </div>
        <div className={s.details}>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-5 text-sm">
            <div>
              <dt className="text-muted-foreground">Requested budget</dt>
              <dd className="mt-1 font-medium">
                {money(campaign.requestedBudgetCents)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Daily limit</dt>
              <dd className="mt-1 font-medium">
                {money(campaign.dailyLimitCents)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Confirmed funding</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums">
                {money(campaign.fundedCents)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Funds remaining</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums">
                {money(campaign.balanceCents)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Reported spend</dt>
              <dd className="mt-1 font-medium">{money(campaign.spentCents)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Leads · Cost per lead</dt>
              <dd className="mt-1 font-medium">
                {campaign.leadCount} ·{" "}
                {campaign.leadCount > 0
                  ? money(Math.round(campaign.spentCents / campaign.leadCount))
                  : "—"}
              </dd>
            </div>
          </dl>
          {(campaign.pendingRefundCents || 0) > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {money(campaign.pendingRefundCents!)} reserved for a pending
              refund.
            </p>
          )}
          {funding.some((payment) => payment.status === "pending") && (
            <p className="mt-3 text-xs text-muted-foreground">
              A payment is awaiting confirmation. Its amount remains reserved
              until Stripe confirms payment or expiry.
            </p>
          )}
          {campaign.reporting && (
            <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
              {campaign.reporting.impressions?.toLocaleString() ?? "—"}{" "}
              impressions · {campaign.reporting.clicks?.toLocaleString() ?? "—"}{" "}
              clicks
              {campaign.reporting.reportedAt &&
                ` · Reported ${dateLabel(campaign.reporting.reportedAt)}`}
              {campaign.reporting.source && ` · ${campaign.reporting.source}`}
            </p>
          )}
          {!operator && !closed && (
            <div className="mt-6 flex flex-wrap gap-2">
              {campaign.status === "awaiting_approval" && creative && (
                <Button
                  className={s.primary}
                  onClick={() => openAction("approve")}
                >
                  Review approval
                </Button>
              )}
              <Button
                variant="outline"
                disabled={
                  !workspace.readiness.fundingAvailable ||
                  campaign.fundedCents - campaign.refundedCents >=
                    campaign.requestedBudgetCents
                }
                onClick={() => openAction("fund")}
              >
                {funding.some((payment) => payment.status === "pending")
                  ? "Continue pending payment"
                  : "Add campaign funds"}
              </Button>
              <Button variant="outline" onClick={() => openAction("change")}>
                Request a change
              </Button>
              {campaign.status === "live" && (
                <Button variant="outline" onClick={() => openAction("pause")}>
                  <Pause />
                  Request pause
                </Button>
              )}
            </div>
          )}
          {!operator && !workspace.readiness.fundingAvailable && !closed && (
            <p className="mt-3 text-xs text-muted-foreground">
              Campaign payments are not available yet. Your request and creative
              review can still proceed.
            </p>
          )}
          {notice && (
            <p role="status" className={s.notice}>
              {notice}
            </p>
          )}
          {action && !operator && (
            <form
              key={action === "approve" ? campaign.creativeVersion : action}
              onSubmit={submit}
              className="mt-5 space-y-4 border-t pt-5"
            >
              {action === "fund" && (
                <>
                  <label className={s.label}>
                    Amount to add (USD)
                    <input
                      className={s.field}
                      name="amount"
                      inputMode="decimal"
                      placeholder="500.00"
                      defaultValue={(
                        (funding.find((payment) => payment.status === "pending")
                          ?.amountCents ??
                          campaign.requestedBudgetCents -
                            campaign.fundedCents +
                            campaign.refundedCents) / 100
                      ).toFixed(2)}
                      required
                    />
                  </label>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    You’ll review payment in Stripe. Funds appear only after
                    payment is confirmed, and belong to this campaign. Funding
                    does not launch an ad.
                  </p>
                </>
              )}
              {action === "change" && (
                <label className={s.label}>
                  What would you like to change?
                  <textarea
                    className={s.field}
                    name="message"
                    rows={3}
                    maxLength={2000}
                    required
                  />
                </label>
              )}
              {action === "approve" && (
                <>
                  <p className="text-sm leading-relaxed">
                    Approve campaign version {campaign.creativeVersion} for{" "}
                    {campaign.states.join(", ")}, with a{" "}
                    {money(campaign.requestedBudgetCents)} campaign budget and{" "}
                    {money(campaign.dailyLimitCents)} daily limit.
                  </p>
                  {campaign.workflowPublished &&
                    !campaign.workflowPublished.paused && (
                      <div className="space-y-3 rounded-lg border p-3">
                        <p className="text-xs font-medium">
                          Included follow-up messages
                        </p>
                        {campaign.workflowPublished.steps.map((step, index) => (
                          <p
                            key={index}
                            className="whitespace-pre-wrap text-xs leading-relaxed"
                          >
                            {index + 1}. {step.text}
                          </p>
                        ))}
                      </div>
                    )}
                  <label className="flex items-start gap-2 text-sm leading-relaxed">
                    <input type="checkbox" required className="mt-1" />I
                    reviewed the image, copy, landing page, and attached
                    follow-up messages, and confirm the claims about me are
                    accurate.
                  </label>
                </>
              )}
              {action === "pause" && (
                <p className="text-sm leading-relaxed">
                  Our team will pause the ad and confirm here. Delivery and
                  spend may continue until the advertising platform completes
                  the pause.
                </p>
              )}
              {mutation.isError && (
                <p role="alert" className="text-sm text-destructive">
                  {errorMessage(mutation.error)}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  className={s.primary}
                  type="submit"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending
                    ? "Working…"
                    : action === "fund"
                      ? "Continue to payment"
                      : action === "approve"
                        ? "Approve campaign"
                        : action === "pause"
                          ? "Send pause request"
                          : "Send request"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={mutation.isPending}
                  onClick={() => setAction(null)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
          {operator && (
            <CampaignOperations
              campaign={campaign}
              ownerId={ownerId}
              funding={funding}
            />
          )}
        </div>
      </div>
      <CampaignFollowup
        campaign={campaign}
        ownerId={ownerId}
        operator={operator}
        available={workspace.readiness.workflowAvailable === true}
        a2pReady={workspace.readiness.a2pReady === true}
      />
      <div className="border-t px-6 pb-6">
        <div className={s.subtabs} aria-label="Campaign records">
          {(["activity", "funds", "leads"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              aria-pressed={section === tab}
              onClick={() => setSection(tab)}
            >
              {tab === "activity"
                ? "Activity"
                : tab === "funds"
                  ? "Funding & spend"
                  : `Leads (${campaign.leadCount})`}
            </button>
          ))}
        </div>
        {section === "activity" && (
          <ol className="max-h-80 overflow-y-auto overscroll-contain divide-y">
            {events.map((event) => (
              <li key={event.id} className="py-4 text-sm">
                <p>{activityText(event)}</p>
                <time
                  className="mt-1 block text-xs text-muted-foreground"
                  dateTime={event.createdAt}
                >
                  {new Date(event.createdAt).toLocaleString()}
                </time>
              </li>
            ))}
            {!events.length && (
              <li className="py-8 text-sm text-muted-foreground">
                No activity recorded yet.
              </li>
            )}
          </ol>
        )}
        {section === "funds" && (
          <div className="max-h-80 overflow-auto overscroll-contain">
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Record</th>
                  <th>Date</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((entry) => (
                  <tr key={entry.id}>
                    <td className="capitalize">
                      {entry.kind.replaceAll("_", " ")}
                      {entry.reference && (
                        <span
                          className="mt-1 block max-w-56 truncate text-xs text-muted-foreground"
                          title={entry.reference}
                        >
                          {entry.reference}
                        </span>
                      )}
                    </td>
                    <td>{dateLabel(entry.createdAt)}</td>
                    <td className="text-right">{money(entry.amountCents)}</td>
                  </tr>
                ))}
                {!ledger.length && (
                  <tr>
                    <td colSpan={3} className="text-muted-foreground">
                      No confirmed payments or spend yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {section === "leads" && (
          <ul className="max-h-80 overflow-y-auto overscroll-contain divide-y">
            {leads.map((lead) => (
              <li
                key={lead.id}
                className="flex flex-wrap items-center justify-between gap-3 py-4 text-sm"
              >
                <div>
                  <p className="font-medium">
                    {lead.name || "Campaign inquiry"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {dateLabel(lead.createdAt)} · Licensedproducer
                  </p>
                </div>
                {!operator && (
                  <Link
                    className="inline-flex min-h-11 items-center gap-2 underline underline-offset-4"
                    href={`${mobile ? "/m" : ""}/leads/${lead.leadId}`}
                  >
                    Open lead
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                )}
              </li>
            ))}
            {!leads.length && (
              <li className="py-8 text-sm text-muted-foreground">
                Campaign inquiries appear here once they’re delivered to your
                CRM.
              </li>
            )}
          </ul>
        )}
      </div>
    </section>
  );
}
